'use client';

import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';

import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { useEffect, useMemo, useState } from 'react';
import { ProductTabsDescription } from './product-tabs-description';
import { ProductTabsParameters } from './product-tabs-parameters';
import { ProductTabsReviews } from './product-tabs-reviews';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';

type TabDef = {
  id: 'description' | 'documents' | 'reviews';
  title: string;
  content: React.ReactNode;
  show: boolean;
};

export function ProductTabs() {
  const product = useVariantsStore(state => state.product) as ProductDetailData;
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const reviewCount = product.ratingCount ?? 0;

  const tabs = useMemo<TabDef[]>(
    () => [
      {
        id: 'description',
        title: 'Popis produktu',
        content: <ProductTabsDescription product={product} selectedVariant={selectedVariant} />,
        show: !!selectedVariant?.description || !!product?.description,
      },
      {
        id: 'documents',
        title: 'Technické parametre',
        content: <ProductTabsParameters product={product} selectedVariant={selectedVariant} />,
        show:
          !!(product?.attributes && product.attributes.length > 0) ||
          !!(selectedVariant?.values && selectedVariant.values.length > 0),
      },
      {
        id: 'reviews',
        title: `Hodnotenie produktu (${reviewCount})`,
        content: <ProductTabsReviews />,
        show: true,
      },
    ],
    [product, selectedVariant, reviewCount],
  );

  const visibleTabs = useMemo(() => tabs.filter(tab => tab.show), [tabs]);

  const [activeTab, setActiveTab] = useState<TabDef['id']>('description');

  useEffect(() => {
    const firstId = visibleTabs[0]?.id;
    if (!firstId) return;

    const isActiveStillVisible = visibleTabs.some(t => t.id === activeTab);
    if (!isActiveStillVisible) setActiveTab(firstId);
  }, [visibleTabs, activeTab]);

  if (visibleTabs.length === 0) return null;

  return (
    <section
      className={cn(
        'custom-section',
        'gap-6 bg-[#F9FAFB] py-6 sm:gap-8 sm:py-8 md:py-16 lg:py-20 xl:py-24',
      )}
    >
      <Tabs
        value={activeTab}
        onValueChange={v => setActiveTab(v as TabDef['id'])}
        className={cn(
          'custom-container',
          'flex flex-col items-center justify-center gap-6 sm:gap-8',
        )}
      >
        <TabsList
          className={cn(
            'overflow-scroll',
            'hide-scroll-bar',
            'flex h-fit w-full flex-nowrap items-center justify-start gap-0 rounded-full border-none bg-white p-2 shadow-none',
          )}
        >
          {visibleTabs.map(tab => (
            <TabsTrigger
              value={tab.id}
              key={tab.id}
              className={cn(
                'flex h-12 w-full items-center justify-center whitespace-nowrap text-center text-sm font-semibold tracking-tight sm:flex-1 lg:text-base',
                'ea-product-tabs__tabs-trigger',
              )}
            >
              {tab.title}
            </TabsTrigger>
          ))}
        </TabsList>
        {visibleTabs.map(tab => (
          <TabsContent key={tab.id} className="w-full" value={tab.id}>
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
}
